import type { Node, XYPosition } from '@xyflow/react';

/** 노드 종류별 기본 크기 (measured 가 아직 없을 때). 캔버스 CSS: 일반 노드 205×~102, 그룹은 GROUP_NODE_DIMENSIONS 와 동일 */
const DEFAULT_SIZE: Record<string, { width: number; height: number }> = {
  subflow: { width: 640, height: 480 },
  group: { width: 600, height: 480 },
  repeatGroup: { width: 560, height: 440 },
  memo: { width: 200, height: 120 },
};
const NORMAL_SIZE = { width: 205, height: 102 };

export interface Rect { x: number; y: number; width: number; height: number }

/** 노드의 실제 크기 — measured → width/height → style → 타입 기본값 순 */
export function nodeSize(node: Partial<Node> & { type?: string }): { width: number; height: number } {
  const m = (node as any).measured;
  if (m?.width && m?.height) return { width: m.width, height: m.height };
  const w = (node as any).width ?? (node.style as any)?.width;
  const h = (node as any).height ?? (node.style as any)?.height;
  if (typeof w === 'number' && typeof h === 'number') return { width: w, height: h };
  return DEFAULT_SIZE[node.type || ''] ?? NORMAL_SIZE;
}

export function nodeRect(node: Node): Rect {
  return { x: node.position.x, y: node.position.y, ...nodeSize(node) };
}

/** 두 사각형이 margin 만큼의 여유를 두고도 겹치는가 */
export function overlaps(a: Rect, b: Rect, margin = 16): boolean {
  return !(
    a.x + a.width + margin <= b.x ||
    b.x + b.width + margin <= a.x ||
    a.y + a.height + margin <= b.y ||
    b.y + b.height + margin <= a.y
  );
}

/**
 * 원하는 위치에서 시작해 다른 노드와 겹치지 않는 가장 가까운 자리를 찾는다.
 * - obstacles: 같은 좌표계(같은 parentId)의 다른 노드들 (자기 자신은 excludeId 로 제외)
 * - 원래 위치가 비어 있으면 그대로 반환. 아니면 step 격자로 나선형 탐색 → 거리순 첫 빈 자리
 * - bounds 가 있으면(그룹 내부) 그 안에서만 찾는다
 */
export function findFreePosition(
  obstacles: Node[],
  desired: XYPosition,
  size: { width: number; height: number },
  opts: { excludeId?: string; step?: number; maxRings?: number; margin?: number; bounds?: Rect } = {},
): XYPosition {
  const { excludeId, step = 40, maxRings = 30, margin = 16, bounds } = opts;
  const others = obstacles.filter(n => n.id !== excludeId).map(nodeRect);
  const inBounds = (p: XYPosition) =>
    !bounds || (p.x >= bounds.x && p.y >= bounds.y && p.x + size.width <= bounds.x + bounds.width && p.y + size.height <= bounds.y + bounds.height);
  const free = (p: XYPosition) => inBounds(p) && !others.some(o => overlaps({ x: p.x, y: p.y, ...size }, o, margin));

  if (free(desired)) return desired;

  // 나선형 후보 (거리순) — 오른쪽/아래 우선이 되도록 같은 거리면 dx>0, dy>0 을 앞에 둔다
  const candidates: XYPosition[] = [];
  for (let ring = 1; ring <= maxRings; ring++) {
    for (let dx = -ring; dx <= ring; dx++) {
      for (let dy = -ring; dy <= ring; dy++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== ring) continue;
        candidates.push({ x: desired.x + dx * step, y: desired.y + dy * step });
      }
    }
    candidates.sort((a, b) => {
      const da = Math.hypot(a.x - desired.x, a.y - desired.y), db = Math.hypot(b.x - desired.x, b.y - desired.y);
      if (Math.abs(da - db) > 0.5) return da - db;
      return (b.x - desired.x) + (b.y - desired.y) - ((a.x - desired.x) + (a.y - desired.y));
    });
    const hit = candidates.find(free);
    if (hit) return hit;
    candidates.length = 0;
  }
  return desired; // 빈 자리를 못 찾으면 원래 위치 (사실상 발생하지 않음)
}

/** 분기 후행 노드 사이의 세로 간격 — 노드 높이와 별개로 줄이 확실히 갈라져 보이는 만큼 */
export const BRANCH_ROW_GAP = 40;
/** 분기 노드와 후행 노드 사이의 가로 간격 */
export const BRANCH_COL_GAP = 60;

/**
 * 분기 노드의 후행 자리를 **위에서 아래로 하나씩** 잡는다.
 *
 * 한꺼번에 여러 개를 더하든 하나씩 더하든 같은 규칙이어야 한다 — 이미 달린 후행이 있으면 그 아래에서 잇는다.
 * 그래서 이 함수는 '몇 번째 줄부터 시작할지' 를 기존 후행의 위치에서 스스로 구한다.
 *
 * @param branch   분기 노드
 * @param existing 이 분기에 이미 달린 후행 노드들 (없으면 분기와 같은 줄에서 시작)
 * @param count    새로 놓을 개수
 * @param size     새 노드 크기
 */
export function branchChildPositions(
  branch: Node, existing: Node[], count: number, size: { width: number; height: number },
): XYPosition[] {
  const x = branch.position.x + nodeSize(branch).width + BRANCH_COL_GAP;
  const rowStep = size.height + BRANCH_ROW_GAP;
  // 첫 줄은 분기와 같은 높이. 이미 후행이 있으면 그중 가장 아래 것의 다음 줄부터.
  let y = branch.position.y;
  if (existing.length) {
    const lowest = Math.max(...existing.map((n) => n.position.y + nodeSize(n).height));
    y = lowest + BRANCH_ROW_GAP;
  }
  return Array.from({ length: Math.max(0, count) }, (_, i) => ({ x, y: y + i * rowStep }));
}
